import { useEffect } from 'react';
import { browserSupport } from './media/probe';
import { ClipsScreen } from './screens/Clips';
import { ExportScreen } from './screens/Export';
import { GuideScreen } from './screens/Guide';
import { MarkScreen } from './screens/Mark';
import { StartScreen } from './screens/Start';
import { SyncScreen } from './screens/Sync';
import { TimestampsScreen } from './screens/Timestamps';
import { useReel } from './state/reel';
import { useApp, type Step as StepId } from './state/store';
import { Button, Chip, Icon, Stepper, type Step } from './ui';
import { useHotkeys } from './ui/hotkeys';
import { TourHost } from './ui/Tour';

export function App() {
  const { step, setStep, project, file, info, exporting, view, openGuide, closeGuide } = useApp();
  const { parsed, usesClock, missingKickoffs, stats } = useReel();
  const isList = project.method === 'list';
  const support = browserSupport();

  // Without a video, only the start screen makes sense.
  useEffect(() => {
    if (!file && step !== 'start') setStep('start');
  }, [file, step, setStep]);

  // The browser's back button closes the guide; a #guide link opens it.
  useEffect(() => {
    const onHash = () => useApp.setState({ view: location.hash.startsWith('#guide') ? 'guide' : 'app' });
    window.addEventListener('popstate', onHash);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onHash);
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  // The export screen owns the running export, so the guide stays closed until it finishes.
  const toggleGuide = () => {
    if (exporting) return;
    if (view === 'guide') closeGuide();
    else openGuide();
  };
  useHotkeys({ 'shift+?': toggleGuide, '?': toggleGuide });

  const order: StepId[] = isList ? ['start', 'timestamps', 'sync', 'clips', 'export'] : ['start', 'mark', 'clips', 'export'];
  const labels: Record<StepId, string> = { start: 'Video', timestamps: 'Timestamps', sync: 'Kickoffs', mark: 'Mark live', clips: 'Clips', export: 'Export' };
  const hasMoments = isList ? parsed.moments.length > 0 || project.marks.length > 0 : project.marks.length > 0;
  const reachable: Record<StepId, boolean> = {
    start: true,
    timestamps: !!info,
    mark: !!info,
    sync: !!info && usesClock && parsed.moments.length > 0,
    clips: !!info && hasMoments && (!usesClock || missingKickoffs.length === 0 || project.marks.length > 0),
    export: !!info && stats.count > 0 && (!usesClock || missingKickoffs.length === 0),
  };
  const currentIndex = order.indexOf(step);
  const steps: Step[] = order.map((id, i) => ({
    label: labels[id],
    state: id === 'sync' && !usesClock ? 'skipped' : id === step && view === 'app' ? 'current' : i < currentIndex ? 'done' : id === step ? 'current' : 'upcoming',
    onClick:
      reachable[id] && !exporting
        ? () => {
            if (view === 'guide') closeGuide();
            setStep(id);
          }
        : undefined,
  }));

  return (
    <div className="app">
      <header className="app-header">
        <button
          type="button"
          className="app-brand"
          onClick={() => {
            if (exporting) return;
            if (view === 'guide') closeGuide();
            setStep('start');
          }}
          aria-label="Highlights, back to start"
        >
          <span className="app-brand-mark">
            <Icon name="play" size={14} />
          </span>
          <span className="app-brand-name">Highlights</span>
        </button>
        <div className="app-header-mid">
          <Stepper steps={steps} />
        </div>
        <div className="app-header-end">
          <span data-tour="privacy">
            <Chip tone="cue" icon="lock">
              On this device
            </Chip>
          </span>
          <span data-tour="guide">
            <Button size="sm" variant={view === 'guide' ? 'secondary' : 'ghost'} icon="info" disabled={exporting} onClick={toggleGuide} aria-pressed={view === 'guide'} kbd="?">
              Guide
            </Button>
          </span>
        </div>
      </header>
      <main className="app-main">
        {view === 'guide' ? (
          <GuideScreen />
        ) : (
          <>
            {!support.ok && step !== 'start' && (
              <div className="app-banner app-banner-danger" role="alert">
                <Icon name="alert" />
                {support.reason}
              </div>
            )}
            {step === 'start' && <StartScreen />}
            {step === 'timestamps' && <TimestampsScreen />}
            {step === 'sync' && <SyncScreen />}
            {step === 'mark' && <MarkScreen />}
            {step === 'clips' && <ClipsScreen />}
            {step === 'export' && <ExportScreen />}
          </>
        )}
      </main>
      <footer className="app-footer">
        <span>Your video is processed in this browser and never uploaded.</span>
        <span aria-hidden="true">·</span>
        <button type="button" disabled={exporting} onClick={toggleGuide} style={{ background: 'none', border: 0, padding: 0, color: 'var(--link)', cursor: 'pointer', fontSize: 13 }}>
          Guide and help
        </button>
        <span aria-hidden="true">·</span>
        <span>Works with Veo timestamps. Not affiliated with Veo.</span>
      </footer>
      <TourHost />
    </div>
  );
}
